import React, { useEffect, useState, lazy, Suspense, useRef } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from './utils/store'
import useSocket from './utils/functions/useSocket'
import ScrollToTop from './components/common/ScrollToTop'
import ProtectedRoute from './components/common/ProtectedRoute'
import GlobalCallNotification from './components/common/GlobalCallNotification'
import AuthRoute from './components/common/AuthRoute'
import Loader from './components/common/Loader'
import { toast } from 'react-toastify'

const Login = lazy(() => import('./pages/Login'))
const Layout = lazy(() => import('./components/layout'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const LoginOtp = lazy(() => import('./pages/LoginOtp'))
const ChangePassword = lazy(() => import('./pages/ChangePassword'))
const Dashboard = lazy(() => import('./pages/AuthPages/Dashboard'))
const Profile = lazy(() => import('./pages/AuthPages/Profile'))
const MyAppointment = lazy(() => import('./pages/AuthPages/MyAppointment'))
const SavedDocument = lazy(() => import('./pages/AuthPages/SavedDocument'))
const SavedDocumentView = lazy(() => import('./pages/AuthPages/SavedDocumentView'))
const BookAppointmentThanks = lazy(() => import('./pages/AuthPages/BookAppointmentThanks'))
const Help = lazy(() => import('./pages/AuthPages/Help'))
const AppointmentDetails = lazy(() => import('./pages/AuthPages/AppointmentDetails'))
const CallNow = lazy(() => import('./pages/AuthPages/CallNow'))
const Chat = lazy(() => import('./pages/AuthPages/Chat'))
const Setting = lazy(() => import('./pages/AuthPages/Setting'))
const Availability = lazy(() => import('./pages/AuthPages/Availability'))
const MyPatients = lazy(() => import('./pages/AuthPages/MyPatients'))

const App = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [incomingCalls, setIncomingCalls] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { connect, listenForEvent, disconnect, sendMessage } = useSocket();
  const { LoginData } = useSelector((state: RootState) => state.auth)

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // the server rejects sockets without a login token and a rejected socket never retries, connect after login
    if (!token) return;
    connect();
    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);
  // ringtone start
  useEffect(() => {
    if (!audioRef.current) {
      const audio = new Audio('/sounds/ringtone.mp3');
      audio.loop = true;
      audio.preload = 'auto';
      audioRef.current = audio;
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (incomingCalls.length > 0) {
      audio.play().catch((err) => {
        // autoplay might be blocked; notify user
        console.warn('Ringtone autoplay blocked:', err);
        toast.info('Tap the screen once to enable call sounds.');
      });
    } else {
      audio.pause();
      try {
        audio.currentTime = 0;
      } catch (e) {
      }
    }
  }, [incomingCalls]);

  // helper to stop ringtone immediately (pass down to GlobalCallNotification)
  const stopRingtone = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    try {
      audio.currentTime = 0;
    } catch (e) {
      // ignore
    }
  };
  // ringtone end
  useEffect(() => {
    if (incomingCalls.length > 0) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  }, [incomingCalls]);


  React.useEffect(() => {
    listenForEvent(`incomingCallListFront`, (data: any) => {
      setIncomingCalls((prevCalls) => {
        const currentCall = data?.waitingList[0];
        if (currentCall && currentCall.callHide === 1 && currentCall.status === 'In progress') {
          return prevCalls.filter(call =>
            call.waitingList[0]?.call_id !== currentCall.call_id &&
            call.waitingList[0]?.patient_id !== currentCall.patient_id
          );
        }
        const isDuplicate = prevCalls.some(call =>
          call.waitingList[0]?.call_id === currentCall?.call_id ||
          call.waitingList[0]?.patient_id === currentCall?.patient_id
        );
        if (!isDuplicate && LoginData?.connectDocId !== null) {
          const filteredWaitingList = data?.waitingList?.filter((waitingListItem: WaitingListItem) =>
            // pcp_doctor_id is only saved when a doctor accepts the call
            (!waitingListItem.pcp_doctor_id || waitingListItem.pcp_doctor_id === LoginData?.connectDocId) &&
            (waitingListItem?.status === 'waiting' || waitingListItem?.status === 'PENDING')
          );
          if (filteredWaitingList.length > 0) {
            return [...prevCalls, data];
          }
        }
        return prevCalls;
      });
    });
  }, [listenForEvent, LoginData, token]);

  return (
    <>
      {/* {
        token && isModalOpen &&
        <GlobalCallNotification
          incomingCalls={incomingCalls}
          setIncomingCalls={setIncomingCalls}
        />
      } */}
      <ScrollToTop />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path='/login' element={
            <AuthRoute>
              <Login />
            </AuthRoute>
          } />
          <Route path='/forgot-password' element={
            <AuthRoute>
              <ForgotPassword />
            </AuthRoute>
          } />
          <Route path='/otp' element={
            <AuthRoute>
              <LoginOtp />
            </AuthRoute>
          } />
          <Route path='/change-password' element={
            <AuthRoute>
              <ChangePassword />
            </AuthRoute>
          } />
          {
            token && <Route path='CallNow' element={<CallNow />} />
          }
          <Route path="/*"
            element={
              <ProtectedRoute>
                <>
                  {
                    token && isModalOpen &&
                    <GlobalCallNotification
                      incomingCalls={incomingCalls}
                      setIncomingCalls={setIncomingCalls}
                    />
                  }
                  <Layout />
                </>
              </ProtectedRoute>
            }>
            <Route path='myaccount/profile' element={<Profile />} />
            <Route path='dashboard' element={<Dashboard />} />
            <Route path='appointments' element={<MyAppointment />} />
            <Route path='MyAppointments/book/thanks' element={<BookAppointmentThanks />} />
            <Route path='SavedDocuments' element={<SavedDocument />} />
            <Route path='SavedDocuments/view' element={<SavedDocumentView />} />
            <Route path='help' element={<Help />} />
            <Route path='availability' element={<Availability />} />
            <Route path='MyAppointments/details' element={<AppointmentDetails />} />
            <Route path='myaccount/settings' element={<Setting />} />
            {/* <Route path='CallNow' element={<CallNow />} /> */}
            <Route path='patient' element={<MyPatients />} />
            <Route path='inbox' element={<Chat />} />
            <Route path='*' element={<Dashboard />} />
            <Route
              path="/*"
              element={<Navigate to={"/dashboard"} replace />}
            />
          </Route>
          <Route
            path="/"
            element={<Navigate to={!token ? "/login" : '/patient'} replace />}
          />
        </Routes>
      </Suspense>
    </>
  )
}

export default App